import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import {
  Button,
  Field,
  Modal,
  Money,
  Select,
  Spinner,
  TextField,
} from "./ui";
import { dollarsToCents, formatDate } from "../lib/format";
import type { Account } from "../lib/models";
import type {
  StatementSummary,
  StatementListRow,
  ReconcileResult,
} from "@shared/types";

/** Editable subset of the parsed summary (all cents). */
interface EditSummary {
  provider: string;
  merchantNumber: string;
  periodStart: string;
  periodEnd: string;
  grossSales: number;
  fees: number;
  chargebacks: number;
  adjustments: number;
  feeBreakdown: { category: string; amount: number }[];
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function StatementsModal({
  account,
  currency,
  onClose,
}: {
  account: Account;
  currency: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { currentOrg } = useOrg();
  const [fileData, setFileData] = useState<{ base64: string; name: string } | null>(
    null,
  );
  const [summary, setSummary] = useState<EditSummary | null>(null);
  const [reconcileStmt, setReconcileStmt] = useState<StatementListRow | null>(null);

  const accountsQ = useQuery({
    queryKey: ["accounts", currentOrg?.id],
    queryFn: () => api.get<Account[]>("/accounts"),
  });

  const list = useQuery({
    queryKey: ["statements", account.id],
    queryFn: () => api.get<StatementListRow[]>(`/accounts/${account.id}/statements`),
  });

  const analyze = useMutation({
    mutationFn: async (file: File) => {
      const dataUrl = await readAsDataUrl(file);
      setFileData({ base64: dataUrl, name: file.name });
      return api.post<StatementSummary>(
        `/accounts/${account.id}/statements/analyze`,
        { fileBase64: dataUrl },
      );
    },
    onSuccess: (s) => {
      setSummary({
        provider: s.provider ?? "",
        merchantNumber: s.merchantNumber ?? "",
        periodStart: s.periodStart ?? "",
        periodEnd: s.periodEnd ?? "",
        grossSales: s.grossSales,
        fees: s.fees,
        chargebacks: s.chargebacks,
        adjustments: s.adjustments,
        feeBreakdown: s.feeBreakdown,
      });
    },
  });

  const commit = useMutation({
    mutationFn: () =>
      api.post(`/accounts/${account.id}/statements/commit`, {
        fileBase64: fileData!.base64,
        fileName: fileData!.name,
        contentType: "application/pdf",
        summary: {
          provider: summary!.provider || null,
          merchantNumber: summary!.merchantNumber || null,
          periodStart: summary!.periodStart || null,
          periodEnd: summary!.periodEnd || null,
          grossSales: summary!.grossSales,
          fees: summary!.fees,
          chargebacks: summary!.chargebacks,
          adjustments: summary!.adjustments,
          feeBreakdown: summary!.feeBreakdown,
        },
      }),
    onSuccess: () => {
      setSummary(null);
      setFileData(null);
      list.refetch();
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });

  async function download(id: string) {
    const res = await fetch(`/api/statements/${id}/file`, {
      headers: currentOrg ? { "X-Org-Id": currentOrg.id } : undefined,
      credentials: "include",
    });
    if (!res.ok) return;
    const url = URL.createObjectURL(await res.blob());
    window.open(url, "_blank");
  }

  const net = summary
    ? summary.grossSales - summary.fees - summary.chargebacks + summary.adjustments
    : 0;

  const dollarField = (value: number, set: (cents: number) => void) => (
    <TextField
      type="number"
      step="0.01"
      className="text-right"
      value={(value / 100).toFixed(2)}
      onChange={(e) => set(dollarsToCents(e.target.value || "0"))}
    />
  );

  return (
    <>
    <Modal open onClose={onClose} title={`Statements — ${account.name}`} wide>
      <div className="space-y-6">
        {/* Upload / review */}
        {!summary ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              Upload a merchant processing statement (PDF). We&apos;ll read the
              totals with AI, you confirm them, and it posts the accounting and
              stores the PDF here.
            </p>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) analyze.mutate(f);
              }}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-gray-50"
            />
            {analyze.isPending && <Spinner label="Reading statement…" />}
            {analyze.error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                Couldn&apos;t read that statement. Check it&apos;s a PDF and try again.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                Review parsed statement
              </h3>
              <span className="text-xs text-gray-400">{fileData?.name}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Provider">
                <TextField
                  value={summary.provider}
                  onChange={(e) => setSummary({ ...summary, provider: e.target.value })}
                />
              </Field>
              <Field label="Merchant number">
                <TextField
                  value={summary.merchantNumber}
                  onChange={(e) =>
                    setSummary({ ...summary, merchantNumber: e.target.value })
                  }
                />
              </Field>
              <Field label="Period start">
                <TextField
                  type="date"
                  value={summary.periodStart}
                  onChange={(e) =>
                    setSummary({ ...summary, periodStart: e.target.value })
                  }
                />
              </Field>
              <Field label="Period end">
                <TextField
                  type="date"
                  value={summary.periodEnd}
                  onChange={(e) =>
                    setSummary({ ...summary, periodEnd: e.target.value })
                  }
                />
              </Field>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-2 text-gray-600">Gross card sales → Sales (income)</td>
                  <td className="w-40 py-2">
                    {dollarField(summary.grossSales, (c) =>
                      setSummary({ ...summary, grossSales: c }),
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 text-gray-600">Fees → Merchant Processing Fees</td>
                  <td className="py-2">
                    {dollarField(summary.fees, (c) =>
                      setSummary({ ...summary, fees: c }),
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 text-gray-600">Chargebacks</td>
                  <td className="py-2">
                    {dollarField(summary.chargebacks, (c) =>
                      setSummary({ ...summary, chargebacks: c }),
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 text-gray-600">Adjustments (signed)</td>
                  <td className="py-2">
                    {dollarField(summary.adjustments, (c) =>
                      setSummary({ ...summary, adjustments: c }),
                    )}
                  </td>
                </tr>
                <tr className="font-semibold">
                  <td className="py-2">Net → {account.name}</td>
                  <td className="py-2 pr-2 text-right">
                    <Money cents={net} currency={currency} />
                  </td>
                </tr>
              </tbody>
            </table>
            {commit.error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                Import failed. Please try again.
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setSummary(null);
                  setFileData(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={() => commit.mutate()} disabled={commit.isPending}>
                {commit.isPending ? "Importing…" : "Import statement"}
              </Button>
            </div>
          </div>
        )}

        {/* Stored statements */}
        <div>
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Stored statements
          </h3>
          {list.isLoading && <Spinner />}
          {list.data && list.data.length === 0 && (
            <p className="text-sm text-gray-400">No statements imported yet.</p>
          )}
          {list.data && list.data.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                  <tr>
                    <th className="px-3 py-2">Period</th>
                    <th className="px-3 py-2">Provider</th>
                    <th className="px-3 py-2 text-right">Gross</th>
                    <th className="px-3 py-2 text-right">Fees</th>
                    <th className="px-3 py-2 text-right">Net</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {list.data.map((s) => (
                    <tr key={s.id}>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                        {s.periodStart ? formatDate(s.periodStart) : "—"} –{" "}
                        {s.periodEnd ? formatDate(s.periodEnd) : "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{s.provider ?? "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <Money cents={s.grossSales} currency={currency} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Money cents={s.fees} currency={currency} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Money cents={s.netAmount} currency={currency} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-3">
                          <button
                            className="text-brand-600 hover:underline"
                            onClick={() => setReconcileStmt(s)}
                          >
                            Reconcile
                          </button>
                          <button
                            className="text-brand-600 hover:underline"
                            onClick={() => download(s.id)}
                          >
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Modal>
    {reconcileStmt && (
      <ReconcilePanel
        statement={reconcileStmt}
        bankOptions={(accountsQ.data ?? []).filter(
          (a) => a.type === "asset" && a.id !== account.id,
        )}
        currency={currency}
        onClose={() => setReconcileStmt(null)}
        onDone={() => {
          setReconcileStmt(null);
          list.refetch();
          qc.invalidateQueries({ queryKey: ["accounts"] });
        }}
      />
    )}
    </>
  );
}

function ReconcilePanel({
  statement,
  bankOptions,
  currency,
  onClose,
  onDone,
}: {
  statement: StatementListRow;
  bankOptions: Account[];
  currency: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const preferred = bankOptions.find(
    (a) => a.subtype === "checking" || a.subtype === "savings",
  );
  const [bankId, setBankId] = useState(preferred?.id ?? bankOptions[0]?.id ?? "");
  const [result, setResult] = useState<ReconcileResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const find = useMutation({
    mutationFn: () =>
      api.post<ReconcileResult>(`/statements/${statement.id}/reconcile/candidates`, {
        bankAccountId: bankId,
      }),
    onSuccess: (r) => {
      setResult(r);
      setSelected(new Set(r.matches.map((m) => m.journalEntryId)));
    },
  });

  const commit = useMutation({
    mutationFn: () =>
      api.post(`/statements/${statement.id}/reconcile/commit`, {
        bankAccountId: bankId,
        journalEntryIds: [...selected],
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Reconcile ${statement.provider ?? "statement"} ${statement.periodStart ?? ""}`}
      wide
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Match this statement&apos;s deposits to your bank account and reclassify
          them as transfers into the merchant clearing account (so they aren&apos;t
          double-counted as income).
        </p>
        <div className="flex items-end gap-3">
          <Field label="Bank account">
            <Select value={bankId} onChange={(e) => setBankId(e.target.value)}>
              {bankOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </Select>
          </Field>
          <Button onClick={() => find.mutate()} disabled={!bankId || find.isPending}>
            {find.isPending ? "Matching…" : "Find matches"}
          </Button>
        </div>

        {find.error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Couldn&apos;t match this statement. Try again.
          </div>
        )}

        {result && (
          <>
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-blue-700">
                {result.matches.length} matched
              </span>
              {result.unmatchedDailyCount > 0 && (
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-amber-700">
                  {result.unmatchedDailyCount} statement days not found (likely
                  settle next period)
                </span>
              )}
              <span
                className={`rounded-full px-2.5 py-0.5 ${result.feeMatched ? "bg-brand-100 text-brand-700" : "bg-gray-100 text-gray-500"}`}
              >
                {result.feeMatched ? "fee matched" : "fee not found in bank"}
              </span>
            </div>

            {result.matches.length === 0 ? (
              <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                No matching bank transactions found in this account for the period.
              </p>
            ) : (
              <div className="max-h-[45vh] overflow-y-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400">
                    <tr>
                      <th className="px-3 py-2 w-10" />
                      <th className="px-3 py-2">Bank date</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2">Currently</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.matches.map((m) => (
                      <tr key={m.journalEntryId}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected.has(m.journalEntryId)}
                            onChange={(e) =>
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(m.journalEntryId);
                                else next.delete(m.journalEntryId);
                                return next;
                              })
                            }
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {formatDate(m.date)}
                        </td>
                        <td className="px-3 py-2 capitalize text-gray-600">{m.kind}</td>
                        <td className="px-3 py-2 text-right">
                          <Money cents={m.amount} currency={currency} />
                        </td>
                        <td className="px-3 py-2 text-gray-500">{m.currentCategory}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {commit.error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                Reclassify failed. Try again.
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => commit.mutate()}
                disabled={selected.size === 0 || commit.isPending}
              >
                {commit.isPending
                  ? "Reclassifying…"
                  : `Reclassify ${selected.size} into ${result.clearingAccountName}`}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
